import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

import DataTable from '../data_table/DataTable';
import { optimizedCellClassName } from '../data_table/tableClasses';
import Skeleton from '../ui/Skeleton';
import { cx } from '../ui/classNames';
import { GROUP_METRICS, formatGroupValue, toNullableNumber } from '../../utils/riskOverviewPresentation';
import { negativeCell } from '../../styles/tailwindClasses';

const getMetricRowId = (row) => row.metricKey;

// One value column: the row metric's `key` (absKey, pctUcapKey or
// pctAumKey) read from the category's actual or selected (OPT) metrics.
// Each side loads independently so Actual values stay visible during a
// saved-portfolio comparison request.
function valueColumn({ id, header, key, source }) {
    const optimized = source === 'selected';
    const valueOf = (row) => row[source]?.[row[key]];
    return {
        id,
        header,
        enableSorting: false,
        cell: ({ row }) => (
            row.original[`${source}Loading`]
                ? <Skeleton width="70%" className="ml-auto" />
                : formatGroupValue(row.original[source], row.original[key])
        ),
        meta: {
            align: 'right',
            cellClassName: ({ row }) => cx(
                optimized && optimizedCellClassName,
                toNullableNumber(valueOf(row.original)) < 0 && negativeCell,
            ),
        },
    };
}

const columns = [
    { id: 'metric', header: 'Metric', accessorFn: (row) => row.label, enableSorting: false },
    valueColumn({ id: 'actualAbs', header: 'Exp.', key: 'absKey', source: 'actual' }),
    valueColumn({ id: 'selectedAbs', header: 'Exp. OPT', key: 'absKey', source: 'selected' }),
    valueColumn({ id: 'actualPctUcap', header: '% UCap', key: 'pctUcapKey', source: 'actual' }),
    valueColumn({ id: 'selectedPctUcap', header: '% UCap OPT', key: 'pctUcapKey', source: 'selected' }),
    valueColumn({ id: 'actualPctAum', header: '% AUM', key: 'pctAumKey', source: 'actual' }),
    valueColumn({ id: 'selectedPctAum', header: '% AUM OPT', key: 'pctAumKey', source: 'selected' }),
];

export default function GroupRiskMetricsTable({
    actualByGroup,
    selectedByGroup,
    selectedGroup,
    selectedMetric,
    onMetricClick,
    actualLoading,
    optimizedLoading,
    groupLabel,
}) {
    const actual = actualByGroup.get(selectedGroup) ?? null;
    const selected = selectedByGroup.get(selectedGroup) ?? null;
    const hasGroupData = Boolean(actual || selected);
    // One row per metric, carrying the category's metrics it reads.
    const rows = useMemo(
        () => GROUP_METRICS.map((metric) => ({
            ...metric, actual, selected, actualLoading, selectedLoading: optimizedLoading,
        })),
        [actual, selected, actualLoading, optimizedLoading],
    );
    // Rows open the metric's chart once the category has data.
    const rowActivation = hasGroupData ? {
        onRowClick: (row) => onMetricClick(row.metricKey),
        getRowProps: (row) => ({ 'aria-label': `Show ${row.label} ${groupLabel.toLowerCase()} chart` }),
    } : null;

    return (
        <div className="relative" aria-busy={actualLoading || optimizedLoading || undefined}>
            <DataTable
                aria-label={`${groupLabel} metrics`}
                data={rows}
                columns={columns}
                getRowId={getMetricRowId}
                tableLayout="auto"
                selectedRowId={selectedMetric}
                className="min-w-[400px]"
                rowHeight={30}
                headerHeight={28}
                {...rowActivation}
            />
        </div>
    );
}

GroupRiskMetricsTable.propTypes = {
    actualByGroup: PropTypes.instanceOf(Map).isRequired,
    selectedByGroup: PropTypes.instanceOf(Map).isRequired,
    selectedGroup: PropTypes.string.isRequired,
    selectedMetric: PropTypes.string.isRequired,
    onMetricClick: PropTypes.func.isRequired,
    actualLoading: PropTypes.bool.isRequired,
    optimizedLoading: PropTypes.bool.isRequired,
    groupLabel: PropTypes.string.isRequired,
};
